import React from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BookOnlineIcon from '@mui/icons-material/BookOnline';

const TEAL = '#65C8D0';
const TEAL_DARK = '#3DADB5';
const TEAL_LIGHT = '#E0F6F8';

const cellSx = {
  fontSize: '0.8rem',
  py: 1,
  px: 1.5,
  borderBottom: `1px solid ${TEAL_LIGHT}`,
  color: '#333',
};

const headCellSx = {
  fontSize: '0.72rem',
  fontWeight: 700,
  py: 1,
  px: 1.5,
  backgroundColor: TEAL_LIGHT,
  color: TEAL_DARK,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  whiteSpace: 'normal',
  lineHeight: 1.3,
};

export default function ViewPrebookingModal({ open, onClose, date, records, onEdit, onDelete }) {
  const showActions = !!onEdit || !!onDelete;
  const theme = useTheme();
  const isMobilePortrait = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileLandscape = useMediaQuery('(max-height: 500px) and (orientation: landscape)');
  const isMobile = isMobilePortrait || isMobileLandscape;

  const formattedDate = date
    ? new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', sm: 680 },
          maxWidth: 680,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '85vh',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BookOnlineIcon sx={{ color: '#fff', fontSize: 22 }} />
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', letterSpacing: 1.2, fontWeight: 600 }}>
                PREBOOKINGS
              </Typography>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                Prebooking Details
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem' }}>
                {formattedDate}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ overflowY: 'auto', flex: 1, px: 2, py: 1.5 }}>
          {records.length === 0 ? (
            <Typography sx={{ textAlign: 'center', color: '#999', fontSize: '0.85rem', py: 5 }}>
              No prebookings for this date.
            </Typography>
          ) : (
            <TableContainer sx={{ overflowX: isMobile ? 'auto' : 'hidden' }}>
              <Table
                size="small"
                stickyHeader
                sx={{
                  width: '100%',
                  ...(isMobile ? { minWidth: 520 } : { tableLayout: 'fixed' }),
                }}
              >
                {!isMobile && (
                  <colgroup>
                    <col style={{ width: showActions ? '19%' : '22%' }} />
                    <col style={{ width: showActions ? '15%' : '18%' }} />
                    <col style={{ width: showActions ? '12%' : '14%' }} />
                    <col style={{ width: showActions ? '32%' : '46%' }} />
                    {showActions && <col style={{ width: '22%' }} />}
                  </colgroup>
                )}
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Title</TableCell>
                    <TableCell sx={headCellSx}>Author</TableCell>
                    <TableCell sx={headCellSx}>Quote No.</TableCell>
                    <TableCell sx={headCellSx}>Description</TableCell>
                    {showActions && <TableCell sx={headCellSx}>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((record, idx) => (
                    <TableRow
                      key={record._id}
                      sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f0fbfc' }}
                    >
                      <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{record.title || '—'}</TableCell>
                      <TableCell sx={cellSx}>{record.author || '—'}</TableCell>
                      <TableCell sx={cellSx}>{record.quote_number || '—'}</TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {record.description || '—'}
                      </TableCell>
                      {showActions && (
                        <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', pr: 2.5 }}>
                          <button
                            type="button"
                            onClick={() => onEdit?.(record)}
                            style={{
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              color: '#fff',
                              background: '#F59E0B',
                              border: 'none',
                              borderRadius: '3px',
                              padding: '2px 7px',
                              cursor: 'pointer',
                              marginRight: 4,
                            }}
                          >
                            ✎ Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete?.(record)}
                            style={{
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              color: '#fff',
                              background: '#EF4444',
                              border: 'none',
                              borderRadius: '3px',
                              padding: '2px 7px',
                              cursor: 'pointer',
                            }}
                          >
                            ✕ Delete
                          </button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

ViewPrebookingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  date: PropTypes.string,
  records: PropTypes.array,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

ViewPrebookingModal.defaultProps = {
  date: '',
  records: [],
};
